import { useState } from "react";

import { HOSTED_APP, normaliseHost, writeHost } from "../host.js";
import { text, useText } from "../i18n.js";
import { CONNECT } from "../text/login.js";

import { LanguagePicker } from "./Login.js";

interface Props {
  /** The address in use; null on the first open, when there is nothing else to show. */
  readonly current: string | null;
  /** Leaves the address as it was; absent on the first open, where there is nowhere else to go. */
  readonly onClose?: () => void;
}

/** How long to wait for the host before saying it cannot be reached. */
export const PROBE_TIMEOUT_MS = 8_000;

const EXAMPLE = "https://desktop.tail1234.ts.net:7702";

function reasonOf(failure: unknown): string {
  const t = text(CONNECT);
  if (failure instanceof DOMException && (failure.name === "TimeoutError" || failure.name === "AbortError")) return t.noAnswerIn(PROBE_TIMEOUT_MS / 1000);
  // fetch says nothing more than this about a refused port, a bad certificate or a host that does not answer the app's origin.
  if (failure instanceof TypeError) return t.noConnection;
  return failure instanceof Error ? failure.message : String(failure);
}

/**
 * The connect screen (client-app §6): where the host is, for the packaged
 * app. The address is tried before it is kept, so a typo is caught here and
 * not as a silent "can't reach your computer" later; a host that is simply
 * off right now can be kept regardless. Kept, the app starts over on it,
 * since everything that reaches the host read the address once at start.
 */
export function Connect({ current, onClose }: Props): React.JSX.Element {
  const t = useText(CONNECT);
  const first = onClose == null;
  // The hosted product is the usual answer, so it is filled in on the first open; a box of your own is typed over it.
  const [value, setValue] = useState(current ?? HOSTED_APP);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** An address that failed its try, offered to keep anyway. */
  const [unreached, setUnreached] = useState<string | null>(null);

  const keep = (host: string): void => {
    writeHost(localStorage, host);
    window.location.reload();
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    const host = normaliseHost(value);
    if (host == null) {
      setError(t.notAnAddress(EXAMPLE));
      setUnreached(null);
      return;
    }
    setBusy(true);
    setError(null);
    setUnreached(null);
    try {
      const response = await fetch(`${host}/api/settings`, { signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
      // The host answers its settings as JSON, a JSON 502 while its box is away, or a JSON 401 when it wants a sign-in first (the hosted product); anything else is some other server.
      const json = (response.headers.get("content-type") ?? "").includes("json");
      if (!json || (!response.ok && response.status !== 502 && response.status !== 401)) throw new Error(json ? t.answered(response.status) : t.answeredNotArkBot(response.status));
      keep(host);
    } catch (failure) {
      setError(t.couldNotReach(host, reasonOf(failure)));
      setUnreached(host);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={`agent-settings connect${first ? " connect-first" : ""}`} onSubmit={(event) => void submit(event)}>
      <div className="settings-head">
        <h2>{first ? t.whereIsIt : t.host}</h2>
        <LanguagePicker />
        {first ? null : (
          <button type="button" className="connect-close" onClick={onClose} aria-label={t.close}>
            ×
          </button>
        )}
      </div>
      <p className="muted">{t.about}</p>
      <label>
        {t.address}
        <input type="text" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoFocus value={value} onChange={(event) => setValue(event.target.value)} placeholder={EXAMPLE} aria-label={t.hostAddress} />
      </label>
      {error != null ? <div className="settings-error">{error}</div> : null}
      <div className="settings-actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? t.trying : t.connect}
        </button>
        {unreached != null ? (
          <button type="button" onClick={() => keep(unreached)}>
            {t.keepAnyway}
          </button>
        ) : null}
        {first ? null : (
          <button type="button" onClick={onClose}>
            {t.cancel}
          </button>
        )}
      </div>
    </form>
  );
}
